import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { productAttributeValueFormSchema } from "@/lib/schemas/product-attribute-schemas";
import { getAttributeColors } from "@/constants/product-attributes";
import { AttributeValue } from "@/types/product-type";

interface ProductAttributeValueFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: any) => void;
  value?: AttributeValue | null;
  attributeName?: string;
  isLoading?: boolean;
}

export const ProductAttributeValueFormDialog = ({
  open,
  onOpenChange,
  onSubmit,
  value,
  attributeName = "",
  isLoading = false,
}: ProductAttributeValueFormDialogProps) => {
  const isEditing = !!value;
  const colors = getAttributeColors(attributeName);
  const hasColorPicker = colors.hasColorPicker;

  const form = useForm<any>({
    resolver: zodResolver(productAttributeValueFormSchema),
    defaultValues: {
      value: "",
      displayValue: "",
      colorCode: "",
      sortOrder: 0,
      isActive: true,
    },
  });

  // Reset form when value changes
  useEffect(() => {
    if (value) {
      form.reset({
        value: value.value,
        displayValue: value.displayValue || "",
        colorCode: value.colorCode || "",
        sortOrder: value.sortOrder,
        isActive: value.isActive,
      });
    } else {
      form.reset({
        value: "",
        displayValue: "",
        colorCode: "",
        sortOrder: 0,
        isActive: true,
      });
    }
  }, [value, form]);

  const handleSubmit = (values: any) => {
    onSubmit(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Attribute Value" : "Add New Value"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the details for this value"
              : `Add a new value for ${attributeName || "this attribute"}`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Value (System)</FormLabel>
                  <FormControl>
                    <Input placeholder="red" {...field} />
                  </FormControl>
                  <FormDescription>
                    Internal value used in the system
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="displayValue"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Display Value</FormLabel>
                  <FormControl>
                    <Input placeholder="Red" {...field} />
                  </FormControl>
                  <FormDescription>
                    How this value will be displayed to users (optional)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {hasColorPicker && (
              <FormField
                control={form.control}
                name="colorCode"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Color Code</FormLabel>
                    <div className="flex items-center gap-2">
                      <FormControl>
                        <Input placeholder="#FF0000" {...field} />
                      </FormControl>
                      {field.value && (
                        <div
                          className="w-10 h-10 rounded-full border border-gray-300"
                          style={{ backgroundColor: field.value }}
                        />
                      )}
                    </div>
                    <FormDescription>
                      Hex color code (e.g., #FF0000 for red)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="sortOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sort Order</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormDescription>
                    Determines the order values are displayed in
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Make this value available for products
                    </FormDescription>
                  </div>
                  <FormControl>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      className="accent-blue-500 h-5 w-5"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || form.formState.isSubmitting}
              >
                {isLoading || form.formState.isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {isEditing ? "Updating..." : "Creating..."}
                  </div>
                ) : isEditing ? (
                  "Update Value"
                ) : (
                  "Create Value"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
